import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ApplicationContext } from '@/v2/state/providers';
import { StateContext } from '@/v2/state/store';
import { actionStrings, MomentStateContext } from '@/v2/state/momentState';
import { IsIOSSafari } from '@/v2/components/utils/DeviceUtils';
import Button from '@/v2/components/atoms/Button';
import BurgerIcon from '@/v2/components/atoms/Icon/Burger';
import Close from '@/v2/components/atoms/Icon/Close';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import { useHistory, useLocation } from 'react-router-dom';

import './Header.scss';

const propTypes = {
    hideLogo: PropTypes.bool,
    hideMenu: PropTypes.bool,
    dark: PropTypes.bool,
    animate: PropTypes.bool,
    show: PropTypes.bool,
    showCloseButton: PropTypes.bool,
};

const defaultProps = {
    hideLogo: false,
    hideMenu: false,
    dark: false,
    animate: false,
    show: false,
    showCloseButton: false,
};

const isBrowserIOSSafari = IsIOSSafari();

const Header = (props) => {
    const { hideLogo, hideMenu, dark, animate, show, showCloseButton } = props;

    const [{ header, overlay }] = useContext(ApplicationContext);

    const store = useContext(StateContext);
    const { dispatch } = store;

    const { trackEvent } = useContext(AnalyticsContext);

    const momentStore = useContext(MomentStateContext);
    const { dispatch: momentDispatch } = momentStore;

    const [shouldRender, setRender] = useState(show);

    const history = useHistory();
    const location = useLocation();

    const HeaderLogo = !hideLogo ? (
        <div className="Header__logo">
            <img src={`${window.HELIOS.publicPath}images/logo.svg`} alt="logo" />
        </div>
    ) : null;

    const goToNavigation = () => {
        trackEvent('vcSteps', { vcStep: 'menu:carousel' });

        if (history.length === 1 || document.referrer !== window.location.host) {
            const { pathname } = location;
            // trim '.html' and add '#', then pass it to carousel
            history.push(`/scenes.html#${pathname.slice(1, pathname.length - 5)}`);
        } else {
            history.goBack();
        }
    };

    const openRegisterInterestOverlay = () => {
        dispatch({ type: 'SHOW_REGISTER_INTEREST_OVERLAY' });
        trackEvent('vcSteps', { vcStep: 'register interest' });
    };

    const onAnimationEnd = () => {
        if (!show) setRender(false);
    };

    useEffect(() => {
        if (show) {
            setRender(true);
        } else if (!show && !animate) {
            setRender(false);
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            momentDispatch({ type: actionStrings.progressBarDarkThemeEnabled, payload: dark });
        }
    }, [dark, show]);

    const classes = classNames({
        Header: true,
        'ios-safari': isBrowserIOSSafari,
        'Header--dark': dark,
        'Header--light': !dark,
        'Header--hideLogo': hideLogo,
        'Header--animate': animate,
        'Header--animate-enter': animate && show,
        'Header--animate-leave': animate && !show,
        'Header--hasOverlay': true,
    });

    const buttonVariation = dark ? 'icon-dark' : 'icon-light';

    const MenuButton = !hideMenu ? (
        <div>
            <Button variant={buttonVariation} largeHitbox="left" icon={<BurgerIcon />} type="button" onClick={goToNavigation}>
                {header.text.cta.menu}
            </Button>
        </div>
    ) : null;

    const CloseButton = showCloseButton ? (
        <div>
            <Button variant={buttonVariation} largeHitbox="left" icon={<Close />} type="button" onClick={window.close}>
                {overlay.text.cta.close}
            </Button>
        </div>
    ) : null;

    if (shouldRender) {
        return (
            <header className={classes}>
                {HeaderLogo}
                <nav className="Header__content" onAnimationEnd={onAnimationEnd}>
                    <div className="Header__menu">
                        {MenuButton}
                        {CloseButton}
                    </div>
                    <div className="Header__register">
                        <Button variant={buttonVariation} largeHitbox="right" type="button" onClick={openRegisterInterestOverlay}>
                            {header.text.cta.register}
                        </Button>
                    </div>
                </nav>
            </header>
        );
    }

    return null;
};

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;

export default Header;
